import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  SecureStorage,
  KeychainAccess
} from '@aparajita/capacitor-secure-storage';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const DEVICE_TOKEN_KEY = 'veritas_student_device_token';

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    'Content-Type': 'application/json'
  };
}

async function parse(response, label) {
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`${label} failed (${response.status}): ${text || response.statusText}`);
  }
  return text ? JSON.parse(text) : null;
}

function toSession(student) {
  if (!student?.id) return null;
  return {
    studentId: student.id,
    studentCode: student.student_code,
    englishName: student.english_name,
    chineseName: student.chinese_name,
    email: student.email,
    isMaster: student.is_master === true,
testingMode: student.is_master === true,
nativeDeviceSession: true
  };
}

async function saveToken(token) {
  await SecureStorage.set(
    DEVICE_TOKEN_KEY,
    token,
    true,
    false,
    KeychainAccess.whenUnlockedThisDeviceOnly
  );
}

async function readToken() {
  const value = await SecureStorage.get(DEVICE_TOKEN_KEY, true, false);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

async function removeToken() {
  try {
    await SecureStorage.remove(DEVICE_TOKEN_KEY, false);
  } catch (err) {
    console.error('Removing secure device token failed:', err);
  }
}

function buildDeviceName(info, platform) {
  const manufacturer = String(info?.manufacturer || '').trim();
  const model = String(info?.model || '').trim();

  const parts = [];

  if (
    manufacturer &&
    !model.toLowerCase().startsWith(manufacturer.toLowerCase())
  ) {
    parts.push(manufacturer);
  }

  if (model) parts.push(model);

  let name = parts.join(' ').trim();

  if (!name) {
    name =
      platform === 'ios'
        ? 'iPhone / iPad'
        : platform === 'android'
          ? 'Android device'
          : 'Mobile device';
  }

  if (info?.isVirtual) {
    name += ' (Emulator)';
  }

  return name;
}

async function getDeviceRegistrationName(platform) {
  try {
    const info = await Device.getInfo();
    return buildDeviceName(info, platform);
  } catch (err) {
    console.error('Reading device information failed:', err);

    return platform === 'ios'
      ? 'iPhone / iPad'
      : platform === 'android'
        ? 'Android device'
        : 'Mobile device';
  }
}

export async function getNativeDeviceToken() {
  return readToken();
}

export async function registerNativeDeviceSession(
  student,
  password,
  { revokeOtherDevices = false } = {}
) {
  const platform = Capacitor.getPlatform();
  const deviceName = await getDeviceRegistrationName(platform);

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/register_student_device`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_student_id: student.id,
        p_password: password,
        p_device_name: deviceName,
        p_platform: platform,
        p_revoke_other: revokeOtherDevices
      })
    }
  );

  const result = await parse(response, 'Registering this device');

  if (!result?.ok || !result?.token || !result?.student) {
    throw new Error(
      result?.status === 'invalid_password'
        ? 'Incorrect password.'
        : 'This device could not be registered for persistent login.'
    );
  }

  try {
    await saveToken(result.token);
  } catch (err) {
    try {
      await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/revoke_student_device`,
        {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ p_token: result.token })
        }
      );
    } catch (_) {}
    throw new Error('Secure device storage is unavailable on this device.');
  }

  localStorage.removeItem('studentSession');
  return toSession(result.student);
}

export async function restoreNativeDeviceSession() {
  let token;
  try {
    token = await readToken();
  } catch (err) {
    throw new Error('The app could not read its saved login from secure device storage.');
  }

  if (!token) return null;

  let response;
  try {
    response = await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/student_session_from_device`,
      {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ p_token: token })
      }
    );
  } catch (err) {
    throw new Error(
      'The app could not connect to verify the saved login. Check your internet connection and try again.'
    );
  }

  const result = await parse(response, 'Restoring saved login');

  if (!result?.ok || !result?.student) {
    await removeToken();
    return null;
  }

  return toSession(result.student);
}

export async function logoutNativeDeviceSession() {
  let token = null;
  try {
    token = await readToken();
  } catch (_) {}

  if (token) {
    try {
      await fetch(
        `${SUPABASE_URL}/rest/v1/rpc/revoke_student_device`,
        {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ p_token: token })
        }
      );
    } catch (err) {
      console.error('Revoking device session failed:', err);
    }
  }

  await removeToken();
  localStorage.removeItem('studentSession');
}

export async function revokeAllStudentDeviceSessions(studentId, password) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/revoke_all_student_devices`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_student_id: studentId,
        p_password: password
      })
    }
  );

  const result = await parse(response, 'Revoking registered devices');

  if (!result?.ok) {
    throw new Error('Registered app devices could not be revoked.');
  }

  return result;
}